import { useState } from 'react';
import { Plus, X, Trash2, Pencil } from 'lucide-react';
import { adminAddFaq, adminUpdateFaq, adminDeleteFaq } from '@/lib/api';
import { inp } from './styles';

export function FaqPanel({ faqs, onRefresh }) {
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  return (
    <div data-testid="faq-panel" className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-slate-400 text-sm">Manage public FAQs. Use the "Order" field to reorder (lower numbers appear first). Use bullets by prefixing a line with "- ".</p>
        <button onClick={() => { setShowAdd(!showAdd); setEditingId(null); }} data-testid="add-faq-btn"
          className="flex items-center gap-2 bg-[#F26522] hover:bg-[#D9551A] text-white px-5 py-2.5 rounded-xl font-medium text-sm transition-all">
          {showAdd ? <X size={16} /> : <Plus size={16} />}
          {showAdd ? 'Cancel' : 'Add FAQ'}
        </button>
      </div>

      {showAdd && <FaqForm onSuccess={() => { setShowAdd(false); onRefresh(); }} nextOrder={(faqs?.length || 0) + 1} />}

      {faqs.map(item => (
        <div key={item.id} data-testid={`admin-faq-${item.id}`}>
          {editingId === item.id ? (
            <FaqForm faq={item} onSuccess={() => { setEditingId(null); onRefresh(); }} onCancel={() => setEditingId(null)} />
          ) : (
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-[#F26522]/10 text-[#F26522] px-2 py-0.5 rounded text-[10px] font-semibold">#{item.order ?? 0}</span>
                  <h4 className="text-white font-semibold">{item.question}</h4>
                </div>
                <div className="text-slate-400 text-xs whitespace-pre-line line-clamp-3">{item.answer}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => { setEditingId(item.id); setShowAdd(false); }} data-testid={`edit-faq-${item.id}`}
                  className="text-slate-400 hover:text-[#F26522] p-2 transition-colors"><Pencil size={16} /></button>
                <button onClick={async () => { await adminDeleteFaq(item.id); onRefresh(); }} data-testid={`delete-faq-${item.id}`}
                  className="text-red-400 hover:text-red-300 p-2 transition-colors"><Trash2 size={16} /></button>
              </div>
            </div>
          )}
        </div>
      ))}

      {faqs.length === 0 && !showAdd && (
        <p className="text-slate-500 text-sm text-center py-8">No FAQs yet. Click "Add FAQ" to create one.</p>
      )}
    </div>
  );
}

function FaqForm({ faq, onSuccess, onCancel, nextOrder = 1 }) {
  const isEdit = !!faq;
  const [question, setQuestion] = useState(faq?.question || '');
  const [answer, setAnswer] = useState(faq?.answer || '');
  const [order, setOrder] = useState(faq?.order ?? nextOrder);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setSaving(true);
    try {
      const data = { question, answer, order: Number(order) };
      if (isEdit) {
        await adminUpdateFaq(faq.id, data);
      } else {
        await adminAddFaq(data);
      }
      onSuccess();
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="faq-form" className="bg-white/[0.03] border border-white/5 rounded-2xl p-6 space-y-4">
      <h4 className="text-white font-semibold">{isEdit ? 'Edit FAQ' : 'New FAQ'}</h4>
      {err && <p className="text-red-400 text-sm">{err}</p>}

      <div>
        <label className="block text-slate-400 text-xs mb-1.5 font-medium">Question</label>
        <input value={question} onChange={e => setQuestion(e.target.value)} required placeholder="e.g. How do I book a cage?" className={inp} data-testid="faq-question" />
      </div>

      <div>
        <label className="block text-slate-400 text-xs mb-1.5 font-medium">Answer</label>
        <textarea value={answer} onChange={e => setAnswer(e.target.value)} required rows={6}
          placeholder={"Plain text. Use a blank line for paragraph break, and lines starting with '- ' for bullets.\n\nExample:\nWe currently offer:\n- Individual Cage Rental (1 Cage)\n- Half Field Rental (3 Cages)"}
          className={inp + " resize-none"} data-testid="faq-answer" />
        <p className="text-slate-500 text-xs mt-1">Tip: lines starting with "- " render as bullets. Blank lines start new paragraphs.</p>
      </div>

      <div className="w-32">
        <label className="block text-slate-400 text-xs mb-1.5 font-medium">Order</label>
        <input type="number" value={order} onChange={e => setOrder(e.target.value)} className={inp} data-testid="faq-order" />
        <p className="text-slate-500 text-xs mt-1">Lower = higher up</p>
      </div>

      <div className="flex gap-3">
        <button type="submit" disabled={saving} data-testid="faq-save-btn"
          className="bg-[#F26522] hover:bg-[#D9551A] disabled:opacity-50 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all">
          {saving ? 'Saving...' : isEdit ? 'Update FAQ' : 'Add FAQ'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="bg-white/5 text-slate-400 hover:text-white px-6 py-3 rounded-xl font-medium text-sm transition-all">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
